/**
 * Copyright 2025 Lumavec Group LLC
 *
 * All rights reserved. No part of this software may be used, copied, modified,
 * or distributed without explicit written permission. This software is provided
 * "as-is" without warranty of any kind, express or implied, including but not
 * limited to warranties of merchantability or fitness for a particular purpose.
 */

import { useState, useEffect, useMemo } from 'react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Label } from '@/components/ui/label';

type Frequency = 'hourly' | 'daily' | 'weekly' | 'monthly';

interface ScheduleSelectorProps {
  value?: string; // cron expression
  onChange: (cronExpression: string) => void;
}

// Days of the week
const DAYS_OF_WEEK = [
  { value: '0', label: 'Sunday' },
  { value: '1', label: 'Monday' },
  { value: '2', label: 'Tuesday' },
  { value: '3', label: 'Wednesday' },
  { value: '4', label: 'Thursday' },
  { value: '5', label: 'Friday' },
  { value: '6', label: 'Saturday' },
];

// Days of the month (1-28 to avoid month-end issues)
const DAYS_OF_MONTH = Array.from({ length: 28 }, (_, i) => ({
  value: String(i + 1),
  label: `${i + 1}${getOrdinalSuffix(i + 1)}`,
}));

// Hours (0-23)
const HOURS = Array.from({ length: 24 }, (_, i) => ({
  value: String(i),
  label: formatHour(i),
}));

// Minutes (in 15-minute increments)
const MINUTES = [
  { value: '0', label: ':00' },
  { value: '15', label: ':15' },
  { value: '30', label: ':30' },
  { value: '45', label: ':45' },
];

// Hour intervals for hourly frequency
const HOUR_INTERVALS = [
  { value: '1', label: 'Every hour' },
  { value: '2', label: 'Every 2 hours' },
  { value: '3', label: 'Every 3 hours' },
  { value: '4', label: 'Every 4 hours' },
  { value: '6', label: 'Every 6 hours' },
  { value: '8', label: 'Every 8 hours' },
  { value: '12', label: 'Every 12 hours' },
];

function getOrdinalSuffix(n: number): string {
  const s = ['th', 'st', 'nd', 'rd'];
  const v = n % 100;
  return s[(v - 20) % 10] || s[v] || s[0];
}

function formatHour(hour: number): string {
  if (hour === 0) return '12:00 AM';
  if (hour === 12) return '12:00 PM';
  if (hour < 12) return `${hour}:00 AM`;
  return `${hour - 12}:00 PM`;
}

/**
 * Parse a cron expression and extract schedule settings.
 * Returns null if the cron expression doesn't match a supported pattern.
 */
function parseCronExpression(cron: string): {
  frequency: Frequency;
  hour: string;
  minute: string;
  dayOfWeek: string;
  dayOfMonth: string;
  hourInterval: string;
} | null {
  if (!cron) return null;

  const parts = cron.trim().split(/\s+/);
  if (parts.length !== 5) return null;

  const [minute, hour, dayOfMonth, month, dayOfWeek] = parts;

  // Hourly: */N * * * * or 0 */N * * *
  if (minute.startsWith('*/') || (minute === '0' && hour.startsWith('*/'))) {
    const interval = hour.startsWith('*/') ? hour.slice(2) : '1';
    return {
      frequency: 'hourly',
      hour: '0',
      minute: '0',
      dayOfWeek: '1',
      dayOfMonth: '1',
      hourInterval: interval,
    };
  }

  // Monthly: M H D * *
  if (dayOfMonth !== '*' && dayOfWeek === '*') {
    return {
      frequency: 'monthly',
      hour: hour === '*' ? '9' : hour,
      minute: minute === '*' ? '0' : minute,
      dayOfWeek: '1',
      dayOfMonth,
      hourInterval: '1',
    };
  }

  // Weekly: M H * * D
  if (dayOfWeek !== '*' && dayOfMonth === '*') {
    return {
      frequency: 'weekly',
      hour: hour === '*' ? '9' : hour,
      minute: minute === '*' ? '0' : minute,
      dayOfWeek,
      dayOfMonth: '1',
      hourInterval: '1',
    };
  }

  // Daily: M H * * *
  if (dayOfMonth === '*' && dayOfWeek === '*') {
    return {
      frequency: 'daily',
      hour: hour === '*' ? '9' : hour,
      minute: minute === '*' ? '0' : minute,
      dayOfWeek: '1',
      dayOfMonth: '1',
      hourInterval: '1',
    };
  }

  return null;
}

/**
 * Generate a cron expression from schedule settings.
 */
function generateCronExpression(
  frequency: Frequency,
  hour: string,
  minute: string,
  dayOfWeek: string,
  dayOfMonth: string,
  hourInterval: string
): string {
  switch (frequency) {
    case 'hourly':
      if (hourInterval === '1') {
        return `0 * * * *`;
      }
      return `0 */${hourInterval} * * *`;
    case 'daily':
      return `${minute} ${hour} * * *`;
    case 'weekly':
      return `${minute} ${hour} * * ${dayOfWeek}`;
    case 'monthly':
      return `${minute} ${hour} ${dayOfMonth} * *`;
    default:
      return '0 9 * * *';
  }
}

/**
 * Get a human-readable description of the schedule.
 */
function getScheduleDescription(
  frequency: Frequency,
  hour: string,
  minute: string,
  dayOfWeek: string,
  dayOfMonth: string,
  hourInterval: string
): string {
  const timeStr = `${formatHour(parseInt(hour))}`.replace(':00', `:${minute.padStart(2, '0')}`);
  
  switch (frequency) {
    case 'hourly':
      if (hourInterval === '1') {
        return 'Every hour';
      }
      return `Every ${hourInterval} hours`;
    case 'daily':
      return `Daily at ${timeStr}`;
    case 'weekly': {
      const dayName = DAYS_OF_WEEK.find(d => d.value === dayOfWeek)?.label || 'Monday';
      return `Weekly on ${dayName} at ${timeStr}`;
    }
    case 'monthly': {
      const dayNum = parseInt(dayOfMonth);
      return `Monthly on the ${dayNum}${getOrdinalSuffix(dayNum)} at ${timeStr}`;
    }
    default:
      return '';
  }
}

/**
 * Convert a cron expression to a human-readable description.
 * Returns a fallback message if the cron expression can't be parsed.
 */
export function cronToHumanReadable(cronExpression: string): string {
  const parsed = parseCronExpression(cronExpression);
  if (!parsed) {
    return 'Custom schedule';
  }
  return getScheduleDescription(
    parsed.frequency,
    parsed.hour,
    parsed.minute,
    parsed.dayOfWeek,
    parsed.dayOfMonth,
    parsed.hourInterval
  );
}

export function ScheduleSelector({ value, onChange }: ScheduleSelectorProps) {
  // Parse initial value or use defaults
  const parsed = useMemo(() => parseCronExpression(value || ''), [value]);
  
  const [frequency, setFrequency] = useState<Frequency>(parsed?.frequency || 'daily');
  const [hour, setHour] = useState(parsed?.hour || '9');
  const [minute, setMinute] = useState(parsed?.minute || '0');
  const [dayOfWeek, setDayOfWeek] = useState(parsed?.dayOfWeek || '1');
  const [dayOfMonth, setDayOfMonth] = useState(parsed?.dayOfMonth || '1');
  const [hourInterval, setHourInterval] = useState(parsed?.hourInterval || '1');

  // Generate and emit cron expression when settings change
  useEffect(() => {
    const cron = generateCronExpression(
      frequency,
      hour,
      minute,
      dayOfWeek,
      dayOfMonth,
      hourInterval
    );
    onChange(cron);
  }, [frequency, hour, minute, dayOfWeek, dayOfMonth, hourInterval, onChange]);

  const description = getScheduleDescription(
    frequency,
    hour,
    minute,
    dayOfWeek,
    dayOfMonth,
    hourInterval
  );

  return (
    <div className="space-y-4">
      {/* Frequency selector */}
      <div className="space-y-2">
        <Label>Frequency</Label>
        <Select value={frequency} onValueChange={(v) => setFrequency(v as Frequency)}>
          <SelectTrigger className="border border-gray-400 ring-1 ring-gray-200">
            <SelectValue placeholder="Select frequency" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="hourly">Hourly</SelectItem>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Hour interval for hourly frequency */}
      {frequency === 'hourly' && (
        <div className="space-y-2">
          <Label>Interval</Label>
          <Select value={hourInterval} onValueChange={setHourInterval}>
            <SelectTrigger className="border border-gray-400 ring-1 ring-gray-200">
              <SelectValue placeholder="Select interval" />
            </SelectTrigger>
            <SelectContent>
              {HOUR_INTERVALS.map((interval) => (
                <SelectItem key={interval.value} value={interval.value}>
                  {interval.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Day of week for weekly frequency */}
      {frequency === 'weekly' && (
        <div className="space-y-2">
          <Label>Day of Week</Label>
          <Select value={dayOfWeek} onValueChange={setDayOfWeek}>
            <SelectTrigger className="border border-gray-400 ring-1 ring-gray-200">
              <SelectValue placeholder="Select day" />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OF_WEEK.map((day) => (
                <SelectItem key={day.value} value={day.value}>
                  {day.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Day of month for monthly frequency */}
      {frequency === 'monthly' && (
        <div className="space-y-2">
          <Label>Day of Month</Label>
          <Select value={dayOfMonth} onValueChange={setDayOfMonth}>
            <SelectTrigger className="border border-gray-400 ring-1 ring-gray-200">
              <SelectValue placeholder="Select day" />
            </SelectTrigger>
            <SelectContent>
              {DAYS_OF_MONTH.map((day) => (
                <SelectItem key={day.value} value={day.value}>
                  {day.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      )}

      {/* Time selection for non-hourly frequencies */}
      {frequency !== 'hourly' && (
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Hour</Label>
            <Select value={hour} onValueChange={setHour}>
              <SelectTrigger className="border border-gray-400 ring-1 ring-gray-200">
                <SelectValue placeholder="Select hour" />
              </SelectTrigger>
              <SelectContent>
                {HOURS.map((h) => (
                  <SelectItem key={h.value} value={h.value}>
                    {h.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <Label>Minute</Label>
            <Select value={minute} onValueChange={setMinute}>
              <SelectTrigger className="border border-gray-400 ring-1 ring-gray-200">
                <SelectValue placeholder="Select minute" />
              </SelectTrigger>
              <SelectContent>
                {MINUTES.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      )}

      {/* Schedule description */}
      <div className="pt-2 border-t">
        <p className="text-sm text-muted-foreground">
          <span className="font-medium">Schedule:</span> {description}
        </p>
      </div>
    </div>
  );
}
